import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const InflationCalculator = () => {
  const [currentAmount, setCurrentAmount] = useState(100000);
  const [inflationRate, setInflationRate] = useState(6);
  const [years, setYears] = useState(20);
  const [calculationType, setCalculationType] = useState('future'); // 'future' or 'past'
  const [result, setResult] = useState(null);

  const calculate = () => {
    const yearlyData = [];
    
    if (calculationType === 'future') {
      // Future value - how much will you need
      let futureValue = currentAmount;
      for (let year = 0; year <= years; year++) {
        yearlyData.push({
          year,
          nominalValue: currentAmount,
          adjustedValue: futureValue,
          purchasingPower: currentAmount / Math.pow(1 + inflationRate / 100, year),
        });
        futureValue = currentAmount * Math.pow(1 + inflationRate / 100, year + 1);
      }
      
      const finalFutureValue = currentAmount * Math.pow(1 + inflationRate / 100, years);
      const purchasingPowerLoss = currentAmount - (currentAmount / Math.pow(1 + inflationRate / 100, years));
      const percentageLoss = (purchasingPowerLoss / currentAmount) * 100;
      
      setResult({
        type: 'future',
        currentAmount,
        finalValue: finalFutureValue,
        purchasingPowerLoss,
        percentageLoss,
        yearlyData,
        equivalentToday: currentAmount,
        equivalentFuture: finalFutureValue,
      });
    } else {
      // Past value - what was the value in past
      // pastValue initialization for potential future use
      // eslint-disable-next-line no-unused-vars
      let pastValue = currentAmount;
      for (let year = 0; year <= years; year++) {
        const adjustedPast = currentAmount / Math.pow(1 + inflationRate / 100, year);
        yearlyData.push({
          year,
          nominalValue: currentAmount,
          adjustedValue: adjustedPast,
        });
      }
      
      const finalPastValue = currentAmount / Math.pow(1 + inflationRate / 100, years);
      const valueIncrease = currentAmount - finalPastValue;
      const percentageIncrease = (valueIncrease / finalPastValue) * 100;
      
      setResult({
        type: 'past',
        currentAmount,
        finalValue: finalPastValue,
        valueIncrease,
        percentageIncrease,
        yearlyData,
        equivalentPast: finalPastValue,
        equivalentToday: currentAmount,
      });
    }
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Inflation Impact Analysis', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Parameter', 'Value']],
      body: [
        ['Calculation Type', result.type === 'future' ? 'Future Value' : 'Past Value'],
        ['Amount', formatCurrencyForPDF(result.currentAmount)],
        ['Inflation Rate', `${inflationRate}% p.a.`],
        ['Time Period', `${years} years`],
        [result.type === 'future' ? 'Future Equivalent' : 'Past Equivalent', formatCurrencyForPDF(result.finalValue)],
        [result.type === 'future' ? 'Purchasing Power Loss' : 'Value Increase', 
          formatCurrencyForPDF(result.type === 'future' ? result.purchasingPowerLoss : result.valueIncrease)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('Inflation_Analysis.pdf');
  };

  const chartData = result ? {
    labels: result.yearlyData.map(d => `Year ${d.year}`),
    datasets: [
      {
        label: result.type === 'future' ? 'Amount Needed (Future)' : 'Value in Past',
        data: result.yearlyData.map(d => d.adjustedValue),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Nominal Amount (Today)',
        data: result.yearlyData.map(d => d.nominalValue),
        borderColor: '#3b82f6',
        borderDash: [5, 5],
        tension: 0.4,
      },
    ]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="Inflation Calculator"
        description="Free Inflation Calculator - Calculate future value of money with inflation. Understand purchasing power."
        keywords="inflation calculator, inflation calculator India, purchasing power calculator, future value calculator, inflation rate calculator"
        canonicalPath="/inflation"
      />
      <div className="calculator-header">
        <h1>📈 Inflation Calculator</h1>
        <p>Understand how inflation affects your money's purchasing power</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Calculation Type</label>
            <div className="tabs">
              <button 
                className={`tab ${calculationType === 'future' ? 'active' : ''}`}
                onClick={() => setCalculationType('future')}
              >
                Future Value Needed
              </button>
              <button 
                className={`tab ${calculationType === 'past' ? 'active' : ''}`}
                onClick={() => setCalculationType('past')}
              >
                Past Value Equivalent
              </button>
            </div>
          </div>

          <div className="input-group">
            <label>Amount (₹)</label>
            <input
              type="number"
              value={currentAmount === '' ? '' : currentAmount}
              onChange={(e) => setCurrentAmount(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <small style={{ color: '#64748b', fontSize: '12px' }}>
              {calculationType === 'future' 
                ? 'Current expense/value you want to maintain'
                : 'Today\'s amount to find past equivalent'}
            </small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Inflation Rate (% p.a.)</label>
              <input
                type="number"
                step="0.5"
                value={inflationRate === '' ? '' : inflationRate}
                onChange={(e) => setInflationRate(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Time Period (Years)</label>
              <input
                type="number"
                value={years === '' ? '' : years}
                onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate Inflation Impact
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                <h3>{result.type === 'future' ? 'You Will Need' : 'Equivalent Value in Past'}</h3>
                <div className="amount">{formatCurrency(result.finalValue)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  {result.type === 'future' 
                    ? `To maintain same purchasing power after ${years} years`
                    : `${years} years ago to equal today's ${formatCurrency(currentAmount)}`}
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Today's Amount</span>
                  <span className="value">{formatCurrency(result.currentAmount)}</span>
                </div>
                <div className="result-item">
                  <span className="label">{result.type === 'future' ? 'Future Amount Needed' : 'Past Equivalent'}</span>
                  <span className="value">{formatCurrency(result.finalValue)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Inflation Rate</span>
                  <span className="value">{inflationRate}% p.a.</span>
                </div>
                <div className="result-item" style={{ background: '#fef2f2' }}>
                  <span className="label">{result.type === 'future' ? 'Purchasing Power Loss' : 'Value Increase'}</span>
                  <span className="value" style={{ color: '#ef4444' }}>
                    {formatCurrency(result.type === 'future' ? result.purchasingPowerLoss : result.valueIncrease)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">{result.type === 'future' ? 'Percentage Loss' : 'Percentage Increase'}</span>
                  <span className="value">
                    {(result.type === 'future' ? result.percentageLoss : result.percentageIncrease).toFixed(1)}%
                  </span>
                </div>
              </div>

              {chartData && (
                <div className="chart-container">
                  <h4>Value Over Time</h4>
                  <Line data={chartData} options={{ 
                    responsive: true, 
                    plugins: { legend: { position: 'bottom' } },
                    scales: {
                      y: {
                        ticks: {
                          callback: (value) => '₹' + (value / 1000).toFixed(0) + 'K'
                        }
                      }
                    }
                  }} />
                </div>
              )}

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download PDF Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter amount and inflation rate to see the impact</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 India's Historical Inflation</h4>
        <ul>
          <li>Average CPI inflation (2014-2024): ~5-6%</li>
          <li>Food inflation can be higher (6-8%)</li>
          <li>Education inflation: 10-12% annually</li>
          <li>Healthcare inflation: 8-10% annually</li>
          <li>Rule of 72: Divide 72 by inflation rate to know when prices double</li>
        </ul>
      </div>
    </div>
  );
};

export default InflationCalculator;
